/**
 * One parked item and what the office can do about it (OPEN-144 §2, §4, §6).
 *
 *   second run ticket (DUPLICATE_CAPTURE)  the compare, then
 *       Keep the saved ticket    REVIEW_ROLES, reason optional, any job status
 *       Use the parked ticket    EDIT_VOLUME_ROLES (not plain Dispatch Manager), reason
 *                                required, only while the job is In Review and the saved
 *                                ticket is unchanged since the park
 *   a taken-back run ticket       both sides verbatim, then Use the parked ticket
 *     (TAKEN_BACK job_capture)    (EDIT_VOLUME_ROLES, reason, only while the job has no run
 *                                 ticket yet) and Dismiss (OPEN-157 B follow-up)
 *   any other kind                both sides verbatim, then Dismiss (REVIEW_ROLES, reason)
 *   already decided               who decided what, when and why
 *
 * Role gates are UX only; the API refuses anything else and the refusal is named.
 */

import React from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@lodex-pro/web-shared';
import { Button } from 'src/components/ui/button';

import { formatTimestamp } from '../../lib/timestamp';
import { useUserProfile, useUserTimezone } from '../../lib/use-user-timezone';
import { CHANGE_SITE_ROLES, EDIT_VOLUME_ROLES, REVIEW_ROLES } from '../../lib/role-gates';
import { SiteChangeCard } from './SiteChangeCard';
import { ParkedCompare } from './ParkedCompare';
import { ReasonDialog } from './ReasonDialog';
import { decisionErrorMessage, kindLabel, resolutionLabel } from './parked-labels';
import {
  SYNC_CONFLICTS_KEY,
  useApplyParked,
  useApplySiteChange,
  useDismissConflict,
  useKeepParked,
  useSyncConflict,
  type SyncConflictDetailView,
} from './use-sync-conflicts';

const CLOSED_JOB_STATUSES = new Set(['CANCELLED', 'REJECTED', 'TO_BE_INVOICED', 'INVOICED']);

const PRE: React.CSSProperties = {
  fontSize: '12px',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  backgroundColor: 'var(--color-bg-tertiary)',
  padding: '8px',
  borderRadius: '6px',
  maxHeight: '320px',
  overflow: 'auto',
};

function useOnError(
  action: 'keep' | 'apply' | 'dismiss' | 'apply-site-change',
): (err: Error) => void {
  const qc = useQueryClient();
  return (err) => {
    const status = err instanceof ApiError ? err.status : null;
    const code = err instanceof ApiError ? err.code : null;
    toast.error(decisionErrorMessage(action, status, code));
    // A 409 means the item or the job moved: show what is true now.
    if (status === 409) void qc.invalidateQueries({ queryKey: SYNC_CONFLICTS_KEY });
  };
}

function Decided({ item, tz }: { item: SyncConflictDetailView; tz: string }): React.JSX.Element {
  return (
    <p role="status" style={{ fontSize: '14px' }}>
      <strong>{resolutionLabel(item.resolution, item.conflictType)}</strong>
      {item.resolvedByName !== null && ` by ${item.resolvedByName}`}
      {item.resolvedAt !== null && ` · ${formatTimestamp(item.resolvedAt, tz)}`}
      {item.resolutionReason !== null && <> · “{item.resolutionReason}”</>}
    </p>
  );
}

function DuplicateActions({ item }: { item: SyncConflictDetailView }): React.JSX.Element {
  const role = useUserProfile().data?.profile.role ?? '';
  const [dialog, setDialog] = React.useState<'keep' | 'apply' | null>(null);
  const keep = useKeepParked(item.id, () => {
    setDialog(null);
    toast.success('Kept the saved ticket');
  });
  const apply = useApplyParked(item.id, () => {
    setDialog(null);
    toast.success('Used the parked ticket. Check the flags again before approving.');
  });
  const keepError = useOnError('keep');
  const applyError = useOnError('apply');

  const canKeep = REVIEW_ROLES.has(role);
  const canUseRole = EDIT_VOLUME_ROLES.has(role);
  const inReview = item.jobStatusCode === 'IN_REVIEW';
  const savedChanged = item.compare?.savedChangedSinceParked === true;
  const useBlocked = !canUseRole
    ? 'Using the parked ticket needs permission to edit ticket volume.'
    : !inReview
      ? 'Only a ticket In Review can be swapped.'
      : savedChanged
        ? 'The saved ticket changed after this one was parked.'
        : null;

  if (!canKeep) return <></>;
  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
      <Button
        variant="outline"
        onClick={(): void => {
          setDialog('keep');
        }}
      >
        Keep the saved ticket
      </Button>
      <Button
        disabled={useBlocked !== null}
        title={useBlocked ?? undefined}
        onClick={(): void => {
          setDialog('apply');
        }}
      >
        Use the parked ticket
      </Button>
      {useBlocked !== null && (
        <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>{useBlocked}</span>
      )}
      <ReasonDialog
        open={dialog === 'keep'}
        title="Keep the saved ticket?"
        body="The saved ticket stays. Every parked second ticket on this job is closed."
        confirmLabel="Keep"
        required={false}
        pending={keep.isPending}
        onClose={(): void => {
          setDialog(null);
        }}
        onConfirm={(reason): void => {
          const r = reason.trim();
          keep.mutate(r === '' ? {} : { reason }, { onError: keepError });
        }}
      />
      <ReasonDialog
        open={dialog === 'apply'}
        title="Use the parked ticket?"
        body="The parked ticket's gauges, tanks and products replace the saved ones. Extra Services, photos and the ticket number stay. Acknowledged flags must be checked again."
        confirmLabel="Use parked ticket"
        required
        pending={apply.isPending}
        onClose={(): void => {
          setDialog(null);
        }}
        onConfirm={(reason): void => {
          apply.mutate({ reason }, { onError: applyError });
        }}
      />
    </div>
  );
}

function ApplySiteChangeAction({ item }: { item: SyncConflictDetailView }): React.JSX.Element {
  const role = useUserProfile().data?.profile.role ?? '';
  const [open, setOpen] = React.useState(false);
  const apply = useApplySiteChange(item.id, () => {
    setOpen(false);
    toast.success('Site changed as the driver asked');
  });
  const onError = useOnError('apply-site-change');
  if (!CHANGE_SITE_ROLES.has(role)) return <></>;
  return (
    <>
      <Button
        onClick={(): void => {
          setOpen(true);
        }}
      >
        Apply the driver’s change
      </Button>
      <ReasonDialog
        open={open}
        title="Apply the driver’s site change?"
        body="The job moves to the driver’s site now, from wherever it is, with the driver’s reason. Tanks with the same name at that site are re-linked; the driver is told if the job is still on the road."
        confirmLabel="Apply"
        required
        pending={apply.isPending}
        onClose={(): void => {
          setOpen(false);
        }}
        onConfirm={(reason): void => {
          apply.mutate({ reason }, { onError });
        }}
      />
    </>
  );
}

/**
 * OPEN-157 B follow-up — a run ticket the old driver sent after the job was taken back from
 * them. "Use" makes it the job's run ticket, as theirs, while the job has none yet; once the
 * new driver has captured, it can only be dismissed.
 */
function UseTakenBackTicketAction({
  item,
  jobHasRunTicket,
}: {
  item: SyncConflictDetailView;
  jobHasRunTicket: boolean;
}): React.JSX.Element {
  const role = useUserProfile().data?.profile.role ?? '';
  const [open, setOpen] = React.useState(false);
  const apply = useApplyParked(item.id, () => {
    setOpen(false);
    toast.success('Used the parked ticket as the job’s run ticket');
  });
  const onError = useOnError('apply');
  if (!REVIEW_ROLES.has(role)) return <></>;
  const blocked = !EDIT_VOLUME_ROLES.has(role)
    ? 'Using the parked ticket needs permission to edit ticket volume.'
    : jobHasRunTicket
      ? 'The job already has a run ticket, so this one can only be dismissed.'
      : null;
  return (
    <>
      <Button
        disabled={blocked !== null}
        title={blocked ?? undefined}
        onClick={(): void => {
          setOpen(true);
        }}
      >
        Use the parked ticket
      </Button>
      {blocked !== null && (
        <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>{blocked}</span>
      )}
      <ReasonDialog
        open={open}
        title="Use the parked ticket?"
        body={`It becomes the job’s run ticket, as ${item.driverName ?? 'the driver who sent it'}’s, and the job moves on as after any run ticket. The new driver’s phone gets the job where it is then. Photos and the signature they sent stay parked.`}
        confirmLabel="Use parked ticket"
        required
        pending={apply.isPending}
        onClose={(): void => {
          setOpen(false);
        }}
        onConfirm={(reason): void => {
          apply.mutate({ reason }, { onError });
        }}
      />
    </>
  );
}

function DismissAction({ item }: { item: SyncConflictDetailView }): React.JSX.Element {
  const role = useUserProfile().data?.profile.role ?? '';
  const [open, setOpen] = React.useState(false);
  const dismiss = useDismissConflict(item.id, () => {
    setOpen(false);
    toast.success('Dismissed');
  });
  const onError = useOnError('dismiss');
  if (!REVIEW_ROLES.has(role)) return <></>;
  const closed = item.jobStatusCode !== null && CLOSED_JOB_STATUSES.has(item.jobStatusCode);
  return (
    <>
      <Button
        variant="outline"
        onClick={(): void => {
          setOpen(true);
        }}
      >
        Dismiss
      </Button>
      <ReasonDialog
        open={open}
        title="Dismiss this parked item?"
        body={
          closed
            ? 'Dismissing records that you saw this. If the bill is wrong, correct it in billing.'
            : 'Dismissing records that you saw this. Nothing on the job changes.'
        }
        confirmLabel="Dismiss"
        required
        pending={dismiss.isPending}
        onClose={(): void => {
          setOpen(false);
        }}
        onConfirm={(reason): void => {
          dismiss.mutate({ reason }, { onError });
        }}
      />
    </>
  );
}

export function ParkedDecision({ id }: { id: string }): React.JSX.Element {
  const tz = useUserTimezone();
  const { data: item, isLoading, isError } = useSyncConflict(id);
  if (isLoading) return <p role="status">Loading…</p>;
  if (isError || item === undefined) return <p>Couldn’t load this parked item.</p>;

  const header = (
    <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', margin: '0 0 6px' }}>
      {kindLabel(item.conflictType)} · parked {formatTimestamp(item.detectedAt, tz)}
      {item.driverName !== null && ` · from ${item.driverName}`}
    </p>
  );

  if (item.status === 'RESOLVED') {
    return (
      <div>
        {header}
        <Decided item={item} tz={tz} />
        {item.compare !== null && <ParkedCompare compare={item.compare} timeZone={tz} />}
      </div>
    );
  }

  if (item.compare !== null) {
    return (
      <div>
        {header}
        <ParkedCompare compare={item.compare} timeZone={tz} />
        <DuplicateActions item={item} />
      </div>
    );
  }

  // #60 (DEC-365) — a driver's site change: in words, with Apply beside Dismiss.
  if (item.siteChange != null) {
    return (
      <div>
        {header}
        <SiteChangeCard view={item.siteChange} timeZone={tz} />
        <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
          <ApplySiteChangeAction item={item} />
          <DismissAction item={item} />
        </div>
      </div>
    );
  }

  return (
    <div>
      {header}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 600 }}>What the phone sent</div>
          <pre style={PRE}>{JSON.stringify(item.driverPayload, null, 2)}</pre>
        </div>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 600 }}>What the server had</div>
          <pre style={PRE}>{JSON.stringify(item.serverState, null, 2)}</pre>
        </div>
      </div>
      {item.takenBackTicket != null ? (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <UseTakenBackTicketAction
            item={item}
            jobHasRunTicket={item.takenBackTicket.jobHasRunTicket}
          />
          <DismissAction item={item} />
        </div>
      ) : (
        <DismissAction item={item} />
      )}
    </div>
  );
}
